import App from 'antd/lib/app';
import React, { useCallback, useMemo, useState } from 'react';
import { set } from 'lodash';
import { useLocation } from 'react-router-dom';
import { ICreateSavedFilter, ISavedFilter, SavedFilterSubType } from '../../../../../components/sidebar-filters/saved-filter/interface';
import { useSavedFiltersList } from '../../../../../components/sidebar-filters/useSavedFiltersList';
import { DEFAULT_PAGINATION } from '../../../../../domain/commonConst';
import {
    useSavedFilterCreateMutation,
    useSavedFilterDeleteMutation,
    useSavedFilterUpdateMutation,
} from '../../../../../domain/saved-filters/queries';
import { usePageLocation } from '../../../../usePageState';
import { PjmLocationStatus } from '../../../../../domain/pjm/dto/enums';
import { SyncStatus } from '../../../../../domain/pjm/dto/SyncStatus';
import {
    checkFilterInvalidValues,
    countNotEmptyFilters,
    transformPjmLocationsSavedFilterValuesFromDto,
    transformPjmLocationsSavedFilterValuesToDto,
} from './utils';
import { useSidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';

export interface IPjmLocationsPageState {
    // Filters
    filter?: {
        summaryInfo?: {
            status?: PjmLocationStatus | PjmLocationStatus[];
            zone?: string | string[];
            EDC?: {
                name?: string | string[];
            };
        };
        siteInfo?: {
            address?: {
                state?: string | string[];
                zipCode?: string | string[];
            };
        };
        syncDrhubStatus?: SyncStatus | SyncStatus[];
    };

    search?: string;

    pagination?: {
        current: number;
        pageSize: number;
    };

    sorter?: {
        field: string;
        order: 'ascend' | 'descend';
    };
}

export interface IPjmLocationsSidebarFilters {
    'summaryInfo.status'?: PjmLocationStatus[];
    'summaryInfo.zone'?: string[];
    'siteInfo.address.state'?: string[];
    'siteInfo.address.zipCode'?: string[];
    'summaryInfo.EDC.name'?: string[];
    syncDrhubStatus?: SyncStatus[];
}

export interface IPjmLocationsSidebarFiltersContext {
    filtersCount: number;
    filters: IPjmLocationsSidebarFilters;
    savedFilters: ISavedFilter[];

    isSavedFiltersLoading: boolean;

    handleFilterChange: (key: string, value: any) => void;
    handleFiltersClear: () => void;

    selectedSavedFilter: ICreateSavedFilter | null;
    isCreateFilterLoading: boolean;
    isUpdateFilterLoading: boolean;
    isDeleteFilterLoading: boolean;

    handleSavedFilterSelect: (filter: ICreateSavedFilter | null) => void;
    handleCreateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleUpdateSavedFilter: (filterData: ICreateSavedFilter) => Promise<void>;
    handleDeleteSavedFilter: (filter: ISavedFilter) => Promise<void>;

    appliedSavedFilter: ISavedFilter | null;
    handleApplySavedFilter: (savedFilter: ISavedFilter) => void;
}

export const PjmLocationSidebarFiltersContext = React.createContext<IPjmLocationsSidebarFiltersContext>({} as IPjmLocationsSidebarFiltersContext);

export const convertPjmLocationsPageStateToFilterState = (pageState: IPjmLocationsPageState): IPjmLocationsSidebarFilters => {
    const result: IPjmLocationsSidebarFilters = {};
    const filterData = pageState.filter || {};

    // Convert status
    if (filterData.summaryInfo?.status !== undefined) {
        result['summaryInfo.status'] = Array.isArray(filterData.summaryInfo?.status)
            ? filterData.summaryInfo?.status
            : [filterData.summaryInfo?.status];
    }

    // Convert zone
    if (filterData.summaryInfo?.zone !== undefined) {
        result['summaryInfo.zone'] = Array.isArray(filterData.summaryInfo?.zone) ? filterData.summaryInfo?.zone : [filterData.summaryInfo?.zone];
    }

    // Convert state
    if (filterData.siteInfo?.address?.state !== undefined) {
        result['siteInfo.address.state'] = Array.isArray(filterData.siteInfo?.address?.state)
            ? filterData.siteInfo?.address?.state
            : [filterData.siteInfo?.address?.state];
    }

    // Convert zip code
    if (filterData.siteInfo?.address?.zipCode !== undefined) {
        result['siteInfo.address.zipCode'] = Array.isArray(filterData.siteInfo?.address?.zipCode)
            ? filterData.siteInfo?.address?.zipCode
            : [filterData.siteInfo?.address?.zipCode];
    }

    // Convert EDC
    if (filterData.summaryInfo?.EDC?.name !== undefined) {
        result['summaryInfo.EDC.name'] = Array.isArray(filterData.summaryInfo?.EDC?.name)
            ? filterData.summaryInfo?.EDC?.name
            : [filterData.summaryInfo?.EDC?.name];
    }

    // Convert DRHUB Sync Status
    if (filterData.syncDrhubStatus !== undefined) {
        result.syncDrhubStatus = Array.isArray(filterData.syncDrhubStatus) ? filterData.syncDrhubStatus : [filterData.syncDrhubStatus];
    }

    return result;
};

export const PjmLocationSidebarFiltersProvider = ({ children }: { children: React.ReactNode }) => {
    const { filtersCount, filters, handleFilterChange, handleFiltersChange, handleFiltersClear } = useSidebarFiltering();

    // Saving Filters
    const {
        savedFilters,
        isSavedFiltersLoading,
        selectedSavedFilter,
        appliedSavedFilter,
        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,
        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    } = usePjmLocationsSavedFilters();

    const onFiltersChange = useCallback(
        (key: string, value: unknown) => {
            handleFilterChange(key, value);
            handleApplySavedFilter(null); // Reset applied filter when filters change
        },
        [handleApplySavedFilter, handleFilterChange]
    );

    // Combine logic for applying saved filters
    const onApplySavedFilter = useCallback(
        (savedFilter: ISavedFilter) => {
            const filterValues = savedFilter.filter_values;

            handleFiltersChange(filterValues);
            handleApplySavedFilter(savedFilter);
        },
        [handleApplySavedFilter, handleFiltersChange]
    );

    const onFiltersClear = useCallback(() => {
        handleFiltersClear();
        handleApplySavedFilter(null);
    }, [handleApplySavedFilter, handleFiltersClear]);

    return (
        <PjmLocationSidebarFiltersContext.Provider
            value={{
                filtersCount,
                filters,
                savedFilters,

                isSavedFiltersLoading,

                handleFilterChange: onFiltersChange,
                handleFiltersClear: onFiltersClear,

                selectedSavedFilter,
                isCreateFilterLoading,
                isUpdateFilterLoading,
                isDeleteFilterLoading,
                handleSavedFilterSelect,
                handleCreateSavedFilter,
                handleUpdateSavedFilter,
                handleDeleteSavedFilter,

                appliedSavedFilter,
                handleApplySavedFilter: onApplySavedFilter,
            }}
        >
            {children}
        </PjmLocationSidebarFiltersContext.Provider>
    );
};

const useSidebarFiltering = () => {
    const location = useLocation();
    const { queryToState, setPageQuery } = usePageLocation();
    const pageState: IPjmLocationsPageState = queryToState(location.search);

    const [filters, setFilters] = useState<IPjmLocationsSidebarFilters>(convertPjmLocationsPageStateToFilterState(pageState));
    const filtersCount = useMemo(() => countNotEmptyFilters(filters), [filters]);

    const handleFilterChange = (key: string, value: unknown) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
        }));

        // update filter object structure
        const filter = { ...pageState.filter };
        set(filter, key, value);

        // Update the URL query parameters
        const updatedPageState = {
            ...pageState,
            filter,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        };

        setPageQuery(updatedPageState);
    };

    const handleFiltersChange = (newFilters: IPjmLocationsSidebarFilters) => {
        setFilters(newFilters);
        setPageQuery({
            ...pageState,
            filter: newFilters,
            pagination: {
                ...pageState.pagination,
                current: DEFAULT_PAGINATION.current,
            },
        });
    };

    const handleFiltersClear = () => {
        setFilters({});

        setPageQuery({
            ...pageState,
            filter: {},
        });
    };

    return {
        filtersCount,
        filters,

        handleFilterChange,
        handleFiltersChange,
        handleFiltersClear,
    };
};

const usePjmLocationsSavedFilters = () => {
    const { notification } = App.useApp();
    const context = useSidebarFilterOptionsContext();

    // This filter state using to track applied saved filter
    const [appliedSavedFilter, setAppliedSavedFilter] = useState<ISavedFilter | null>(null);

    // This filter state using to create/update saved filter
    const [selectedSavedFilter, setSelectedSavedFilter] = useState<ICreateSavedFilter | null>(null);
    const { savedFilters, isLoading: isSavedFiltersLoading } = useSavedFiltersList(SavedFilterSubType.pjmLocations);

    const { mutateAsync: createSavedFilter, isLoading: isCreateFilterLoading } = useSavedFilterCreateMutation();
    const { mutateAsync: deleteSavedFilter, isLoading: isDeleteFilterLoading } = useSavedFilterDeleteMutation();
    const { mutateAsync: updateSavedFilter, isLoading: isUpdateFilterLoading } = useSavedFilterUpdateMutation();

    const validateSavedFilter = useCallback(
        (filter: ISavedFilter) => {
            // Transform filter values from DTO format to Interface
            filter.filter_values = transformPjmLocationsSavedFilterValuesFromDto(filter.filter_values);

            const outdatedFields = checkFilterInvalidValues(filter.filter_values, context);
            // Check if the filter has not existing values in options
            filter.isOutdated = outdatedFields.length > 0;
            filter.outdatedFields = outdatedFields;

            return filter;
        },
        [context]
    );

    const validatedSavedFilters = useMemo(() => {
        if (savedFilters.length === 0) return [];

        return savedFilters.map((filter: any) => validateSavedFilter(filter));
    }, [savedFilters, validateSavedFilter]);

    const handleSavedFilterSelect = (filter: ICreateSavedFilter | null) => {
        setSelectedSavedFilter(filter);
    };

    const handleApplySavedFilter = (savedFilter: ISavedFilter | null) => {
        setAppliedSavedFilter(savedFilter);
    };

    const handleCreateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformPjmLocationsSavedFilterValuesToDto(filterData.filter_values);

            await createSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot save filter!' });
        }
    };

    const handleUpdateSavedFilter = async (filterData: ICreateSavedFilter) => {
        try {
            // Transform filter values to DTO format
            filterData.filter_values = transformPjmLocationsSavedFilterValuesToDto(filterData.filter_values);

            await updateSavedFilter(filterData);
        } catch (err) {
            notification.error({ message: 'Cannot update filter!' });
        }
    };

    const handleDeleteSavedFilter = async (filter: ISavedFilter) => {
        try {
            await deleteSavedFilter(filter.id);

            if (selectedSavedFilter?.id === filter.id) {
                setSelectedSavedFilter(null);
            }

            if (appliedSavedFilter?.id === filter.id) {
                setAppliedSavedFilter(null);
            }
        } catch (err) {
            notification.error({ message: 'Cannot delete filter!' });
        }
    };

    return {
        savedFilters: validatedSavedFilters,
        selectedSavedFilter,
        appliedSavedFilter,
        isSavedFiltersLoading,

        isCreateFilterLoading,
        isUpdateFilterLoading,
        isDeleteFilterLoading,

        handleSavedFilterSelect,
        handleCreateSavedFilter,
        handleUpdateSavedFilter,
        handleDeleteSavedFilter,
        handleApplySavedFilter,
    };
};

export const usePjmLocationsSidebarFiltersContext = () => {
    const context = React.useContext(PjmLocationSidebarFiltersContext);
    if (!context) {
        throw new Error('usePjmLocationsSidebarFiltersContext must be used within PjmLocationSidebarFiltersContext');
    }

    return context;
};
